<template>
  <v-form ref="form" v-model="valid">
    <v-card flat>
      <div class="close-btn">
        <v-btn @click="closeMe" icon>
          <v-icon>close</v-icon>
        </v-btn>
      </div>
      <v-card-text>
        <v-card flat>
          <v-card-text>
            <v-layout align-center class="green--text">
              <h2>
                {{
                  currentInternshipData
                    ? $t('internship.FormEdit')
                    : $t('internship.FormNew')
                }}
              </h2>
            </v-layout>
          </v-card-text>
        </v-card>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs3>
              <span class="caption grey--text text--darken-1">{{
                $t('internship.ResidencyYear')
              }}</span>
              <v-text-field
                v-model="formData.residencyYear"
                :rules="mandatoryRule"
                type="number"
                class="mr-4 mb-2 thin-border"
                required
                hide-details
                solo
                style="max-width: 100px"
              />
            </v-flex>
            <v-flex xs9>
              <span class="caption grey--text text--darken-1">{{
                $t('internship.Internship')
              }}</span>
              <ComboBox
                v-model="formData.internship"
                :items="internshipSelections"
                itemValue="value"
                itemText="text"
              />
            </v-flex>
          </v-layout>
          <span class="caption grey--text text--darken-1">{{
            $t('profile.Hospital')
          }}</span>
          <ComboBox
            v-if="displayHospital"
            v-model="formData.hospital"
            :items="hospitalSelections"
            itemValue="id"
            itemText="name"
          />

          <v-select
            v-if="displayHealthCenter"
            v-model="formData.healthCenter"
            :label="$t('profile.HealthCenterGroup')"
            :items="healthCenterGroupSelections"
            solo
            class="thin-border"
          />

          <v-text-field
            v-if="formData.internship === 'other'"
            v-model="formData.internshipText"
            :rules="mandatoryRule"
            :label="$t('common.other') + ' (texto livre)'"
            type="text"
            required
          />
          <div v-if="displayRotations">
            <span class="caption grey--text text--darken-1">{{
              $t('internship.Rotation')
            }}</span>
            <v-select
              v-model="formData.rotation"
              :items="rotationSelections"
              :hint="$t('common.Optional')"
              persistent-hint
              clearable
              solo
              class="thin-border"
            />
          </div>
          <div class="mt-2">
            <span class="caption grey--text text--darken-1">{{
              $t('internship.Ward')
            }}</span>
            <v-text-field
              v-model="formData.ward"
              :rules="mandatoryRule"
              :persistent-hint="!!formData.ward ? false : true"
              :hint="$t('common.RequiredField')"
              solo
              class="thin-border"
              required
            />
          </div>
          <v-divider />
          <div class="mb-2 mt-3">
            <span class="caption grey--text text--darken-1"
              >Duração do estágio:</span
            ><br />
            <DateRange
              :startDate.sync="formData.startDate"
              :endDate.sync="formData.endDate"
              singleDatePicker="range"
            />
          </div>
          <span class="caption grey--text text--darken-1">{{
            $t('internship.Tutor')
          }}</span>
          <div class="card-rounded border grey--border px-3 pb-4 mb-2">
            <v-text-field
              v-model="formData.tutor"
              :label="$t('internship.TutorName')"
              hide-details
            >
              <Icon name="person" size="24px" slot="prepend" />
            </v-text-field>
            <v-text-field
              v-model="formData.tutorEmail"
              :label="$t('internship.TutorEmail')"
              hide-details
            >
              <Icon name="mail" size="24px" slot="prepend" />
            </v-text-field>
          </div>
          <span class="caption grey--text text--darken-1">{{
            $t('internship.Status')
          }}</span>
          <v-select
            v-model="formData.status"
            :items="statusItems"
            :hint="$t('common.Optional')"
            persistent-hint
            clearable
            solo
            class="thin-border"
          />

          <v-text-field
            :disabled="formData.status != 'evaluation-done'"
            v-model="formData.grade"
            :label="$t('internship.Grade')"
            :persistent-hint="formData.status !== 'evaluation-done'"
            :hint="gradeHint"
            @keyup="handleInput($event)"
          >
          </v-text-field>

          <v-checkbox
            label="Estágio Internacional?"
            v-model="formData.internacional"
          />

          <file-upload
            :file-url-uploaded.sync="formData.fileUrlAttached"
            :file-name-uploaded.sync="formData.fileNameAttached"
            :path-to-files="filepath"
          ></file-upload>
        </v-card-text>
        <v-card-actions>
          <v-btn
            :disabled="!valid"
            :loading="loading"
            color="secondary"
            @click="submit"
            depressed
            round
            >{{ $t('common.Submit') }}</v-btn
          >
          <v-btn flat color="red" round @click="closeMe">{{
            $t('common.Cancel')
          }}</v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import cms from '@/content'
import { Internship } from '../../models/Specialty'
import { mapGetters, mapActions } from 'vuex'
import Validations from '@/helpers/Validations'
import { Hospital, HealthCenterGroup } from '../../models/Content'
import FileUpload from '@/components/files/FileUpload'
import ComboBox from '@/components/inputs/ComboBox'
import DateRange from './DateRange.vue'

export default {
  components: {
    FileUpload,
    DateRange,
    ComboBox
  },
  name: 'InternshipForm',
  props: {
    closeMe: {
      required: true,
      type: Function
    },
    currentInternshipData: {
      default: null
    }
  },
  data() {
    return {
      formData: null,
      loading: false,
      valid: false,
      grade: null,
      prevGrade: null
    }
  },
  computed: {
    ...mapGetters(['user', 'internships', 'specialty']),
    filepath() {
      return `${this.$store.getters.profile.id}/internships`
    },
    hospitalSelections() {
      return Hospital.query().get()
    },
    internshipSelections() {
      return Internship.query()
        .get()
        .map(i => {
          const text =
            this.specialty === i.specialtyId
              ? i.label
              : `${i.label} (${i.specialtyName})`

          return { value: i.id, text }
        })
    },
    rotationSelections() {
      return cms.getRotations(this.$i18n.locale)
    },
    statusItems() {
      return cms.getStatus(this.$i18n.locale)
    },
    mandatoryRule() {
      return [v => !!v || this.$t('common.RequiredField')]
    },
    monthValidation() {
      return [v => (1 <= v && v <= 12) || this.$t('common.MonthRule')]
    },
    displayRotations() {
      return this.specialty === 'general-surgery'
    },
    healthCenterGroupSelections() {
      return HealthCenterGroup.query().get()
    },
    displayHealthCenter() {
      return (
        this.specialty === 'family-medicine' &&
        [
          'family-medicine-i',
          'family-medicine-ii',
          'family-medicine-iii',
          'family-medicine-iv',
          'infant-health',
          'women-health'
        ].includes(this.formData.internship)
      )
    },
    displayHospital() {
      return ![
        'family-medicine-i',
        'family-medicine-ii',
        'family-medicine-iii',
        'family-medicine-iv'
      ].includes(this.formData.internship)
    },
    gradeHint() {
      if (!this.formData.status) {
        return this.$t('internship.SelectGrade')
      } else if (this.formData.status !== 'evaluation-done') {
        return ''
      } else {
        return this.isLessThan20()
          ? this.$t('common.Optional')
          : 'Deve ser menor que 20'
      }
    }
  },
  methods: {
    ...mapActions([
      'addInternship',
      'updateInternship',
      'showSuccessSnackbar',
      'showErrorSnackbar',
      'getInternships'
    ]),
    isNumber(evt) {
      return Validations.isNumber(evt)
    },
    isNumberOrHifen(evt) {
      return Validations.isNumberOrHifen(evt)
    },
    isLessThan20() {
      if (this.formData.grade <= 20) {
        return true
      }
      return false
    },
    handleInput(e) {
      if (Validations.isDecimalNumber(e)) {
        this.formData.grade = this.prevGrade
      }
      this.prevGrade = this.formData.grade
    },
    submit() {
      this.loading = true
      const addOrUpdate = this.currentInternshipData
        ? this.updateInternship
        : this.addInternship
      let entry = { ...this.formData, userId: this.$store.getters.user.id }
      entry.hospital = this.formData.hospital.id

      entry.internship = this.formData.internship.value
        ? this.formData.internship.value
        : this.formData.internship

      addOrUpdate(entry)
        .then(() => {
          this.showSuccessSnackbar(this.$t('internship.InternshipAdded'))
        })
        .catch(err => {
          this.showErrorSnackbar(err.message || 'Something went wrong')
        })
        .finally(() => {
          this.loading = false
          this.closeMe()
        })
    }
  },
  created() {
    if (this.currentInternshipData) {
      this.formData = {
        ...this.currentInternshipData,
        residencyYear: this.$store.getters.residencyYear
      }
    } else {
      this.formData = {
        residencyYear: this.$store.getters.residencyYear
      }
    }
  },
  watch: {
    'formData.status'(val) {
      if (val !== 'evaluation-done') {
        this.$set(this.formData, 'grade', null)
      }
    }
  }
}
</script>

<style scoped>
.theme--light.v-input--is-disabled input {
  color: black !important;
}
.close-btn {
  position: absolute;
  right: 0px;
  z-index: 10;
}
</style>
